import PropTypes from 'prop-types';
import React, { useContext } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Dropdown, Menu } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSadTear, faCrown, faFileAlt, faSignOutAlt, faQuestionCircle, faLayerGroup } from '@fortawesome/free-solid-svg-icons';
import {
  MODIFY_ATTRIBUTE_TYPE,
  MODIFY_DEAD_ATTRIBUTE,
  VIEW_ADVANCED_DOCUMENTATION,
  VIEW_AUDIT_LOG,
} from 'common/constants/privileges';
import { LoggedInUserContext } from 'common/contexts';
import { getPrivilegesFromUser } from 'common/utils/user';
import { Privilege } from 'common/constants/types';
import logo from './tribe-logo.png';

import SearchFilter from './components/SearchFilter';
import Avatar from '../Avatar';
import './Header.css';
import BackToHomeButton from './components/BackToHomeButton';

const docUrl = process.env.REACT_APP_DOCS_URL;
const adminDocUrl = process.env.REACT_APP_ADMIN_DOCS_URL;
const supportEmail = process.env.REACT_APP_SUPPORT_EMAIL;
const iconStyle = { marginRight: '10px' };
const linkStyle = { margin: '5px 0' };

interface HeaderOptionsProps { privileges: Privilege[] }
const HeaderOptions = ({ privileges }: HeaderOptionsProps) => (
  <Menu>
    {privileges.includes(MODIFY_ATTRIBUTE_TYPE) && (
      <Menu.Item>
        <Link style={linkStyle} to="/admin/attributes">
          <FontAwesomeIcon icon={faCrown} style={iconStyle} />
          Attribute Editor
        </Link>
      </Menu.Item>
    )}
    {privileges.includes(MODIFY_ATTRIBUTE_TYPE) && (
      <Menu.Item>
        <Link style={linkStyle} to="/admin/derived_attributes">
          <FontAwesomeIcon icon={faLayerGroup} style={iconStyle} />
          Derived Attribute Editor
        </Link>
      </Menu.Item>
    )}
    {privileges.includes(MODIFY_DEAD_ATTRIBUTE) && (
      <Menu.Item>
        <Link style={linkStyle} to="/admin/dead_attributes">
          <FontAwesomeIcon icon={faSadTear} style={iconStyle} />
          Dead Attribute Editor
        </Link>
      </Menu.Item>
    )}

    {privileges.includes(VIEW_AUDIT_LOG) && (
      <Menu.Item>
        <Link style={linkStyle} to="/admin/audit-log">
          <FontAwesomeIcon icon={faFileAlt} style={iconStyle} />
          Audit Log
        </Link>
      </Menu.Item>
    )}
    {privileges.includes(VIEW_ADVANCED_DOCUMENTATION) && (
      <Menu.Item>
        <a href={adminDocUrl} style={linkStyle}>
          <FontAwesomeIcon icon={faFileAlt} style={iconStyle} />
          Admin Documentation
        </a>
      </Menu.Item>
    )}
    {docUrl && (
      <Menu.Item>
        <a href={docUrl} style={linkStyle}>
          <FontAwesomeIcon icon={faFileAlt} style={iconStyle} />
          Documentation
        </a>
      </Menu.Item>
    )}
    {supportEmail && (
      <Menu.Item>
        <a href={`mailto:${supportEmail}`} style={linkStyle}>
          <FontAwesomeIcon icon={faQuestionCircle} style={iconStyle} />
          Email Support
        </a>
      </Menu.Item>
    )}
    <Menu.Item>
      <Link style={linkStyle} to="/logout">
        <FontAwesomeIcon icon={faSignOutAlt} style={iconStyle} />
        Log Out
      </Link>
    </Menu.Item>
  </Menu>
);

interface Props {
  avatarUrl?: string;
  name?: string;
}

const HeaderView = ({ avatarUrl, name }: Props) => {
  const { loggedInUser } = useContext(LoggedInUserContext);
  const location = useLocation();

  const privileges = getPrivilegesFromUser(loggedInUser);

  return (
    <div
      className="header"
      style={{
        borderBottom: '1px solid #e0e0e0',
        display: 'flex',
        height: '100px',
        justifyContent: 'space-between',
        paddingLeft: '20px',
        paddingRight: '20px',
        textAlign: 'center',
      }}
    >
      <div style={{ alignItems: 'center', display: 'flex', marginLeft: '10px', marginRight: '10px' }}>
        <img alt="avatar" src={logo} style={{ maxWidth: '100px', maxHeight: '50px' }} />
      </div>
      {location.pathname !== '/users' ? <BackToHomeButton /> : <SearchFilter />}
      <div style={{ alignItems: 'center', display: 'flex', marginLeft: '10px', marginRight: '10px' }}>
        <Dropdown overlay={<HeaderOptions privileges={privileges} />} trigger={['click']}>
          <div style={{ cursor: 'pointer', height: '60px', width: '60px' }}>
            <Avatar imageUrl={avatarUrl} name={name} />
          </div>
        </Dropdown>
      </div>
    </div>
  );
};

HeaderView.propTypes = {
  name: PropTypes.string.isRequired,
};

export default HeaderView;
